import { LitElement, html, css, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { consume } from '@lit/context'
import { repeat } from 'lit/directives/repeat.js'
import { shopperContext, type ShopperContextType } from '../../contexts/shopper'
import { breakpointContext } from '../../contexts/breakpoint'
import { BREAKPOINT } from '../../util/breakpoints'
import { QuoteService } from '../../services/quote-service'
import { isEmbeddedFlow } from '../../util/embedded'
import type { QuoteRequest } from '../../services/quote-service'
import { AepService } from '../../services/aep.service'
import { SessionService } from '../../services/session.service'
import '../shared/continue-button'
import './sections/coverage-selection'
import './sections/primary-subscriber-form'
import './cards/spouse-card'
import './cards/dependent-card'

const ENROLLMENT_FOR_OPTIONS = [
  { value: 'myself', label: 'For myself only' },
  { value: 'myself-family', label: 'For myself, spouse, and / or dependent' },
  { value: 'dependent-over-18', label: 'For a dependent who is over 18' },
  { value: 'dependent-under-18', label: 'For a dependent who is under 18' },
  {
    value: 'someone-else-legal',
    label: 'For someone else and I have legal representation',
  },
  {
    value: 'someone-else-no-legal',
    label:
      "For someone else and I don't have legal representation / guardianship",
  },
]

const ENROLLMENT_TO_SELF_QUESTION: Record<string, string> = {
  myself: 'SELF_ONLY',
  'myself-family': 'SELF_FAM',
  'dependent-under-18': 'DEP_U18',
  'dependent-over-18': 'DEP_O18',
  'someone-else-legal': 'REP_LEGAL',
  'someone-else-no-legal': 'REP_NOLEG',
}

const NEEDS_SESSION_CHECK_VALUES = [
  'dependent-over-18',
  'dependent-under-18',
  'someone-else-legal',
  'someone-else-no-legal',
]

@customElement('ancillary-form')
export class AncillaryForm extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .overlay {
      position: fixed;
      inset: 0;
      background: rgba(255, 255, 255, 0.6);
      backdrop-filter: blur(2px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }
    .rq-continue-button-container {
      display: flex;
      justify-content: flex-end;
    }
    .zip-warning {
      display: flex;
      flex-direction: row;
      gap: 16px;
      border-radius: 8px;
      padding: 24px;
      background: var(--lh-color-warning-500, #f9e719);
    }
  `

  @consume({ context: shopperContext, subscribe: true })
  @state()
  shopperContextValue?: ShopperContextType

  @consume({ context: breakpointContext, subscribe: true })
  breakpoint!: BREAKPOINT
  @state()
  private isLoading = false

  // On mount, initialize fields from context
  connectedCallback() {
    super.connectedCallback()
    this.syncFormDataFromContext()
    // Initialize button disabled state based on current form state
    this.continueButtonDisabled = this.disableContinue || this.isLoading
  }

  get isMobile() {
    return (
      this.breakpoint === BREAKPOINT.PHONE ||
      this.breakpoint === BREAKPOINT.TABLET
    )
  }

  get isEmbedded() {
    return isEmbeddedFlow()
  }

  updated(changedProps: Map<string, any>) {
    super.updated?.(changedProps)
    if (changedProps.has('shopperContextValue')) {
      this.syncFormDataFromContext()
      // Update button state after syncing form data
      this.continueButtonDisabled = this.disableContinue || this.isLoading
    }
  }

  private syncFormDataFromContext() {
    const fd = this.shopperContextValue?.formData
    if (!fd) return
    this.dentalSelected = fd.dentalSelected
    this.visionSelected = fd.visionSelected
    this.showSpouseCard = fd.showSpouseCard
    this.dependentCards = fd.dependentCards
    this.primaryDob = fd.primaryDob
    this.startDate = fd.startDate
    this.spouseData = fd.spouseData
    // Restore agent fields - only fallback to current value if undefined (not empty string)
    this.firstName = fd.firstName !== undefined ? fd.firstName : this.firstName
    this.lastName = fd.lastName !== undefined ? fd.lastName : this.lastName
    this.email = fd.email !== undefined ? fd.email : this.email
    this.phone = fd.phone !== undefined ? fd.phone : this.phone
    this.deliveryMethod =
      fd.deliveryMethod !== undefined ? fd.deliveryMethod : this.deliveryMethod
    this.emailRequired = this.deliveryMethod === 'e'
    this.enrollmentFor =
      fd.enrollmentFor !== undefined ? fd.enrollmentFor : this.enrollmentFor
    // Convert array to Map for dependentsData
    if (fd.dependentsData && Array.isArray(fd.dependentsData)) {
      this.dependentsData = new Map(
        fd.dependentsData.map((d) => [
          d.id,
          {
            dob: d.dob,
            dentalChecked: d.dentalChecked,
            visionChecked: d.visionChecked,
          },
        ])
      )
    }
    // Recheck session if enrollmentFor requires it
    if (
      this.showEnrollmentForSection &&
      NEEDS_SESSION_CHECK_VALUES.includes(this.enrollmentFor)
    ) {
      this.checkActiveSession()
    }
  }

  private persistFormData() {
    if (!this.shopperContextValue) return
    const formData = {
      dentalSelected: this.dentalSelected,
      visionSelected: this.visionSelected,
      showSpouseCard: this.showSpouseCard,
      dependentCards: this.dependentCards,
      primaryDob: this.primaryDob,
      startDate: this.startDate,
      spouseData: this.spouseData,
      // Persist agent fields
      firstName: this.firstName,
      lastName: this.lastName,
      email: this.email,
      phone: this.phone,
      deliveryMethod: this.deliveryMethod,
      enrollmentFor: this.enrollmentFor,
      dependentsData: Array.from(this.dependentsData.entries()).map(
        ([id, d]) => ({ id, ...d })
      ),
    }
    // Dispatch event to container to update context
    this.dispatchEvent(
      new CustomEvent('update-form-data', {
        detail: { formData },
        bubbles: true,
        composed: true,
      })
    )
  }

  @state()
  private dentalSelected = false

  @state()
  private visionSelected = false

  @state()
  private showSpouseCard = false

  @state()
  private dependentCards: number[] = []

  @state()
  private primaryDob = ''

  @state()
  private primaryDobOverAge = false

  @state()
  private startDate = ''

  @state()
  private spouseData: {
    dob: string
    dentalChecked?: boolean
    visionChecked?: boolean
    overAge?: boolean
  } = {
    dob: '',
    dentalChecked: undefined,
    visionChecked: undefined,
    overAge: false,
  }

  @state()
  private emailRequired = false

  @state()
  private email: string = ''

  @state()
  private deliveryMethod: 'e' | 'p' | '' = ''

  @state()
  private firstName: string = ''

  @state()
  private lastName: string = ''

  @state()
  private phone: string = ''

  @state()
  private dependentsData: Map<
    number,
    {
      dob: string
      dentalChecked?: boolean
      visionChecked?: boolean
      overAge?: boolean
    }
  > = new Map()

  @state()
  private dependentFormValid = true

  @state()
  private continueButtonDisabled = false

  @state()
  private errorMessage: string = ''

  @state()
  private enrollmentFor: string = ''

  @state()
  private hasActiveSession: boolean | null = null

  @state()
  private sessionCheckDone = false

  @state()
  private firstNameError: boolean = false

  @state()
  private lastNameError: boolean = false

  private showEnrollmentForSection = true

  private enrollmentSelectBgObserver?: MutationObserver

  private handleEnrollmentForChange(event: CustomEvent) {
    const value = (event as any).detail?.value || ''
    this.enrollmentFor = value
    if (
      this.showEnrollmentForSection &&
      NEEDS_SESSION_CHECK_VALUES.includes(value)
    ) {
      this.checkActiveSession()
    } else {
      this.hasActiveSession = null
      this.sessionCheckDone = false
    }
    this.continueButtonDisabled = this.disableContinue || this.isLoading
    this.persistFormData()
    this.requestUpdate()
  }

  private async checkActiveSession() {
    try {
      this.hasActiveSession = await SessionService.hasActiveSession()
    } finally {
      this.sessionCheckDone = true
    }
  }

  private async handleLogout() {
    await SessionService.logout()
    this.hasActiveSession = false
    this.requestUpdate()
  }

  private handleDentalChange(event: CustomEvent) {
    this.dentalSelected = event.detail.checked
    this.continueButtonDisabled = this.disableContinue || this.isLoading
    this.persistFormData()
    this.requestUpdate()
  }

  private handleVisionChange(event: CustomEvent) {
    this.visionSelected = event.detail.checked
    this.continueButtonDisabled = this.disableContinue || this.isLoading
    this.persistFormData()
    // Force Lit to re-evaluate all computed properties and rendering
    this.requestUpdate()
  }

  private handlePrimaryDobChange(event: CustomEvent) {
    this.primaryDob = (event as any).detail?.value || ''
    this.primaryDobOverAge = (event as any).detail?.overAge || false
    this.continueButtonDisabled = this.disableContinue || this.isLoading
    this.persistFormData()
    this.requestUpdate()
  }

  private handleStartDateChange(event: CustomEvent) {
    this.startDate = (event as any).detail?.value || ''
    this.continueButtonDisabled = this.disableContinue || this.isLoading
    this.persistFormData()
    this.requestUpdate()
  }

  private handleQuoteDeliveryMethodChange(event: CustomEvent) {
    const value = (event as any).detail?.value
    this.emailRequired = value === 'agent-email'
    this.deliveryMethod =
      value === 'agent-pabol' ? 'p' : value === 'agent-email' ? 'e' : ''
    this.continueButtonDisabled = this.disableContinue || this.isLoading
    this.persistFormData()
    this.requestUpdate()
  }

  private handleEmailChange(event: CustomEvent) {
    this.email = (event as any).detail?.value || ''
    this.continueButtonDisabled = this.disableContinue || this.isLoading
    this.persistFormData()
    this.requestUpdate()
  }

  private handleFirstNameChange(event: CustomEvent) {
    this.firstName = (event as any).detail?.value || ''
    this.firstNameError = (event as any).detail?.hasError || false
    this.continueButtonDisabled = this.disableContinue || this.isLoading
    this.persistFormData()
    this.requestUpdate()
  }

  private handleLastNameChange(event: CustomEvent) {
    this.lastName = (event as any).detail?.value || ''
    this.lastNameError = (event as any).detail?.hasError || false
    this.continueButtonDisabled = this.disableContinue || this.isLoading
    this.persistFormData()
    this.requestUpdate()
  }

  private handlePhoneChange(event: CustomEvent) {
    this.phone = (event as any).detail?.value || ''
    this.continueButtonDisabled = this.disableContinue || this.isLoading
    this.persistFormData()
    this.requestUpdate()
  }

  private handleSpouseDataChange(event: CustomEvent) {
    const detail = (event as any).detail
    this.spouseData = {
      dob: detail.dob !== undefined ? detail.dob : this.spouseData.dob,
      dentalChecked:
        detail.dentalChecked !== undefined
          ? detail.dentalChecked
          : this.spouseData.dentalChecked,
      visionChecked:
        detail.visionChecked !== undefined
          ? detail.visionChecked
          : this.spouseData.visionChecked,
      overAge:
        detail.overAge !== undefined ? detail.overAge : this.spouseData.overAge,
    }
    this.continueButtonDisabled = this.disableContinue || this.isLoading
    this.persistFormData()
    this.requestUpdate()
  }

  private handleDependentDataChange(event: CustomEvent) {
    const detail = (event as any).detail
    const id = detail.id
    const existing = this.dependentsData.get(id) || {
      dob: '',
      dentalChecked: undefined,
      visionChecked: undefined,
      overAge: false,
    }

    // Always create a new Map instance for reactivity
    const newDependentsData = new Map(this.dependentsData)
    newDependentsData.set(id, {
      dob: detail.dob !== undefined ? detail.dob : existing.dob,
      dentalChecked:
        detail.dentalChecked !== undefined
          ? detail.dentalChecked
          : existing.dentalChecked,
      visionChecked:
        detail.visionChecked !== undefined
          ? detail.visionChecked
          : existing.visionChecked,
      overAge: detail.overAge !== undefined ? detail.overAge : existing.overAge,
    })
    this.dependentsData = newDependentsData

    // Update the validation state to trigger template re-evaluation
    this.dependentFormValid = !detail.overAge && !!detail.dob
    // Update the button disabled state which will trigger re-render of the button binding
    this.continueButtonDisabled = this.disableContinue || this.isLoading
    this.persistFormData()
    this.requestUpdate()
  }

  private handleSpouseClose() {
    this.showSpouseCard = false
    this.spouseData = {
      dob: '',
      dentalChecked: undefined,
      visionChecked: undefined,
      overAge: false,
    }
    this.continueButtonDisabled = this.disableContinue || this.isLoading
    this.persistFormData()
    this.requestUpdate()
  }

  private handleDependentClose(event: CustomEvent) {
    const id = (event as any).detail?.id
    if (id !== undefined) {
      this.dependentCards = this.dependentCards.filter(
        (cardId) => cardId !== id
      )
      this.dependentsData.delete(id)
      this.dependentsData = new Map(this.dependentsData)
      this.continueButtonDisabled = this.disableContinue || this.isLoading
      this.persistFormData()
      this.requestUpdate()
    }
  }

  private async handleContinue() {
    // Persist form data before navigation
    this.persistFormData()

    const formData = this.collectFormData()

    try {
      this.isLoading = true
      this.continueButtonDisabled = true
      this.errorMessage = '' // Clear any previous errors
      const data = await QuoteService.getQuote(
        formData,
        this.shopperContextValue?.producerSession || null
      )

      // Check if response contains an error array
      if (
        data?.error &&
        Array.isArray(data.error) &&
        data.error[0] &&
        data.error[0].length > 0
      ) {
        this.errorMessage = data.error[0]
        this.isLoading = false
        this.continueButtonDisabled = this.disableContinue || this.isLoading
        return
      }

      // Dispatch event directly to document to update context at the container level
      document.dispatchEvent(
        new CustomEvent('quote-data-updated', {
          detail: { quoteData: data },
          bubbles: true,
          composed: true,
        })
      )

      // Wait a tick for context to update before navigating
      await new Promise((resolve) => setTimeout(resolve, 0))

      // Navigation is handled by the container on quote-data-updated.
      // Keep loader active until route changes.
    } catch (error) {
      console.error('Failed to get quote:', error)
      this.errorMessage =
        'An error occurred while getting your quote. Please try again.'
      this.isLoading = false
    }
  }

  private handleAddSpouse() {
    this.showSpouseCard = true
    this.continueButtonDisabled = this.disableContinue || this.isLoading
    this.persistFormData()
    this.requestUpdate()
  }

  private handleAddDependent() {
    if (this.dependentCards.length < 8) {
      const newId =
        this.dependentCards.length > 0
          ? Math.max(...this.dependentCards) + 1
          : 1
      this.dependentCards = [...this.dependentCards, newId]
      this.continueButtonDisabled = this.disableContinue || this.isLoading
      this.persistFormData()
      this.requestUpdate()
    }
  }

  private formatDateToMMDDYYYY(date: string): string {
    // Convert from YYYY-MM-DD to MM/DD/YYYY
    if (!date) return ''
    if (date.includes('/')) return date // Already in correct format

    const parts = date.split('-')
    if (parts.length === 3) {
      return `${parts[1]}/${parts[2]}/${parts[0]}`
    }
    return date
  }

  private collectFormData(): QuoteRequest {
    // Build dependents array: always include spouse, then dependents
    const dependents: any[] = []

    // Spouse
    if (this.showSpouseCard) {
      dependents.push({
        birthday: this.formatDateToMMDDYYYY(this.spouseData.dob),
        gender: 'F',
        dependentType: 'Spouse',
        handicap: false,
        ssn: '',
        firstName: '',
        lastName: '',
        isDental:
          typeof this.spouseData.dentalChecked === 'boolean'
            ? this.spouseData.dentalChecked
            : false,
        isVision:
          typeof this.spouseData.visionChecked === 'boolean'
            ? this.spouseData.visionChecked
            : false,
      })
    }

    // Dependents
    this.dependentCards.forEach((dependentId) => {
      const data = this.dependentsData.get(dependentId)
      if (data) {
        dependents.push({
          birthday: this.formatDateToMMDDYYYY(data.dob),
          gender: 'M',
          dependentType: 'Child',
          handicap: false,
          ssn: '',
          firstName: '',
          lastName: '',
          isDental:
            typeof data.dentalChecked === 'boolean'
              ? data.dentalChecked
              : false,
          isVision:
            typeof data.visionChecked === 'boolean'
              ? data.visionChecked
              : false,
        })
      }
    })

    const isAgent = !!this.shopperContextValue?.isAgent

    const request: QuoteRequest = {
      offerCrossSale: true,
      visionPlan: this.visionSelected,
      dentalPlan: this.dentalSelected,
      primaryBirthday: this.formatDateToMMDDYYYY(this.primaryDob),
      primaryGender: 'M',
      benefitStartDate: this.formatDateToMMDDYYYY(this.startDate),
      effectiveDate: this.formatDateToMMDDYYYY(this.startDate),
      typeofdental:
        this.showSpouseCard || this.dependentCards.length > 0
          ? 'FAMILY'
          : 'INDIVIDUAL',
      applicationType:
        this.visionSelected && this.dentalSelected
          ? 'vision'
          : this.visionSelected
            ? 'vision'
            : 'dental',
      defaultApplication: null,
      dependents,
      isAgent,
      selfQuestion:
        ENROLLMENT_TO_SELF_QUESTION[this.enrollmentFor] ||
        (isAgent || this.isEmbedded ? 'SELF_ONLY' : undefined),
    }

    if (isAgent) {
      request.firstName = this.firstName || undefined
      request.lastName = this.lastName || undefined
      request.email = this.email || ''
      const normalizedPhone = (this.phone || '').replace(/\D/g, '')
      request.phone = normalizedPhone || ''
      // Only include delivery method when agent selects it (not embedded)
      if (!this.isEmbedded) {
        request.deliveryMethod = this.deliveryMethod || undefined
      }
    }

    return request
  }

  get disableContinue() {
    // Reference dependentFormValid to ensure re-evaluation when it changes
    this.dependentFormValid
    if (this.showEnrollmentForSection) {
      // For direct-shopper (non-agent, non-embedded) flows, require enrollmentFor
      if (
        !this.shopperContextValue?.isAgent &&
        !this.isEmbedded &&
        !this.enrollmentFor
      )
        return true
    }
    // Must have at least one plan
    if (!this.dentalSelected && !this.visionSelected) return true
    // Must have primary DOB and start date, and primary must not have age validation errors
    if (!this.primaryDob || !this.startDate || this.primaryDobOverAge)
      return true
    // If spouse card is shown, must have spouse DOB and must not have age validation errors
    if (
      this.showSpouseCard &&
      (!this.spouseData.dob || this.spouseData.overAge)
    )
      return true
    // If any dependent, all must have DOB and must not be over age
    for (const id of this.dependentCards) {
      const dep = this.dependentsData.get(id)
      if (!dep || !dep.dob || dep.overAge) return true
    }
    // For agent flows (excluding embedded), require first and last name
    if (
      this.shopperContextValue?.isAgent &&
      (!this.firstName || !this.lastName)
    )
      return true
    // Check for first name and last name validation errors
    if (this.firstNameError || this.lastNameError) return true
    // For agent flows (excluding embedded), require a quote delivery method selection
    if (
      this.shopperContextValue?.isAgent &&
      !this.isEmbedded &&
      !this.deliveryMethod
    )
      return true
    // Require email if quote delivery is via agent-email or embedded flow
    if ((this.emailRequired || this.isEmbedded) && !this.email) return true
    // If pnumber is present without psale=y, require phone
    if (this.isEmbedded && !this.phone) return true
    return false
  }

  firstUpdated() {
    this.ensureEnrollmentForSelectBgWhite()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.enrollmentSelectBgObserver?.disconnect()
    this.enrollmentSelectBgObserver = undefined
  }

  private ensureEnrollmentForSelectBgWhite() {
    const selectEl = this.shadowRoot?.getElementById(
      'primary-subscriber-enrollment-for'
    ) as (HTMLElement & { shadowRoot?: ShadowRoot }) | null

    if (!selectEl) {
      this.enrollmentSelectBgObserver?.disconnect()
      this.enrollmentSelectBgObserver = undefined
      return
    }

    const apply = () => {
      const control = selectEl.shadowRoot?.querySelector(
        '.lh-select__control'
      ) as HTMLElement | null
      const content = selectEl.shadowRoot?.querySelector(
        '.lh-select__content'
      ) as HTMLElement | null
      if (control) control.style.backgroundColor = '#ffffff'
      if (content) content.style.backgroundColor = '#ffffff'
    }

    // Apply immediately (or after upgrade)
    if (!selectEl.shadowRoot) {
      setTimeout(apply, 0)
    } else {
      apply()
    }

    // Observe internal changes and re-apply to keep white even in error state
    if (selectEl.shadowRoot && !this.enrollmentSelectBgObserver) {
      this.enrollmentSelectBgObserver = new MutationObserver(() => {
        apply()
      })
      this.enrollmentSelectBgObserver.observe(selectEl.shadowRoot, {
        attributes: true,
        childList: true,
        subtree: true,
      })
    }
  }

  render() {
    this.ensureEnrollmentForSelectBgWhite()
    return html`<lh-container
      gutters="none"
      spacing="lg"
      context-id="ancillary-rate-quote-context"
    >
      ${
        this.errorMessage
          ? html`<lh-alert variant="error" inline dismissible>
              ${unsafeHTML(this.errorMessage)}
            </lh-alert>`
          : nothing
      }

      <lh-typography
        type="heading"
        size=${this.isMobile ? 'xl' : 'lg'}
      >
        Let's start your quote
      </lh-typography>

      ${
        !this.shopperContextValue?.isAgent || this.isEmbedded
          ? html`<lh-typography size="md">
              Select any dental or vision coverage you'd like to add for
              yourself and / or your spouse / dependents.<br />To add coverage
              for a spouse or dependent without the primary applicant, apply for
              a stand-alone plan.
            </lh-typography>`
          : nothing
      }
      ${
        this.shopperContextValue?.isAgent && !this.isEmbedded
          ? html`<lh-typography size="md">
              Enter your client's information below to create a customized rate
              quote. If you are meeting with your client in person, you may
              display the quote for your client's review and then proceed to
              apply online (select <em>Agent submitted - PABOL</em> as the quote
              delivery method). If your client is at another location, you may
              e-mail the client a link to view the rate quote results. A client
              at another location may proceed to apply online at their
              convenience.
            </lh-typography>`
          : nothing
      }
      ${
        !this.shopperContextValue?.isAgent || this.isEmbedded
          ? html`<lh-container
              gutters="none"
              margin-top="xxs"
              margin-bottom="xxs"
            >
              <lh-typography size="md">
                <strong>Note:</strong> You must first add coverage for yourself
                if you want to add it for a spouse or dependent.
              </lh-typography>
            </lh-container>`
          : nothing
      }
      ${
        !this.shopperContextValue?.isAgent &&
        !this.isEmbedded &&
        this.showEnrollmentForSection
          ? html`<lh-container
              gutters="none"
              margin-top="sm"
              margin-bottom="sm"
            >
              <lh-grid gap="none">
                <lh-grid-item>
                  <lh-select
                    required
                    id="primary-subscriber-enrollment-for"
                    name="enrollmentFor"
                    @lh-change=${this.handleEnrollmentForChange}
                  >
                    Who are you completing this enrollment application for?
                    <lh-option
                      slot="options"
                      value=""
                      ?default-selected=${!this.enrollmentFor}
                      >Select one</lh-option
                    >
                    ${ENROLLMENT_FOR_OPTIONS.map(
                      (opt) =>
                        html`<lh-option
                          slot="options"
                          value=${opt.value}
                          ?default-selected=${this.enrollmentFor === opt.value}
                          >${opt.label}</lh-option
                        >`
                    )}
                  </lh-select>
                </lh-grid-item>
                <lh-grid-item></lh-grid-item>
              </lh-grid>

              ${NEEDS_SESSION_CHECK_VALUES.includes(this.enrollmentFor) &&
              this.sessionCheckDone &&
              !this.hasActiveSession
                ? html`<lh-alert variant="warning" inline>
                    <lh-typography
                      >You can only apply for yourself or someone else you're
                      legally authorized to represent.</lh-typography
                    >

                    <lh-typography
                      >To apply for someone else, create an account in their
                      name after selecting a plan.</lh-typography
                    >
                  </lh-alert>`
                : nothing}
              ${NEEDS_SESSION_CHECK_VALUES.includes(this.enrollmentFor) &&
              this.sessionCheckDone &&
              this.hasActiveSession
                ? html`<lh-alert variant="info" inline>
                    <lh-typography
                      >You can only apply for yourself or someone else you're
                      legally authorized to represent.</lh-typography
                    >
                    <lh-typography
                      >To apply for this person, log out and create an account
                      in their name to begin their enrollment
                      application.</lh-typography
                    >
                    <lh-typography>
                      <lh-button
                        variant="text"
                        icon="caret-right"
                        @click=${this.handleLogout}
                      >
                        Log out
                      </lh-button>
                    </lh-typography>
                  </lh-alert>`
                : nothing}
            </lh-container>`
          : nothing
      }

      <lh-container gutters="none" margin-top="sm" margin-bottom="sm">
        <coverage-selection
          .dentalSelected=${this.dentalSelected}
          .visionSelected=${this.visionSelected}
          .isAgent=${!!this.shopperContextValue?.isAgent && !this.isEmbedded}
          .deliveryMethod=${this.deliveryMethod}
          @dental-change=${this.handleDentalChange}
          @vision-change=${this.handleVisionChange}
          @quote-delivery-method-change=${this.handleQuoteDeliveryMethodChange}
        >
        </coverage-selection>
      </lh-container>

      <lh-container
        gutters="none"
        margin-top=${this.isMobile ? 'xl' : 'lg'}
        margin-bottom="sm"
      >
        <primary-subscriber-form
          .effectiveDateOptions=${AepService.getEffectiveDateOptions(
            this.shopperContextValue?.aepCheckData || null
          )}
          .dob=${this.primaryDob}
          .startDate=${this.startDate}
          .isAgent=${!!this.shopperContextValue?.isAgent}
          .isEmbedded=${this.isEmbedded}
          .emailRequired=${this.isEmbedded || this.emailRequired}
          .phoneRequired=${this.isEmbedded}
          .firstName=${this.firstName}
          .lastName=${this.lastName}
          .email=${this.email}
          .phone=${this.phone}
          @dob-change=${this.handlePrimaryDobChange}
          @start-date-change=${this.handleStartDateChange}
          @email-change=${this.handleEmailChange}
          @first-name-change=${this.handleFirstNameChange}
          @last-name-change=${this.handleLastNameChange}
          @phone-change=${this.handlePhoneChange}
        >
        </primary-subscriber-form>
      </lh-container>

      ${
        this.showSpouseCard
          ? html`<lh-container
              gutters="none"
              margin-top=${this.isMobile ? 'xl' : 'sm'}
            >
              <spouse-card
                .dentalSelected=${this.dentalSelected}
                .visionSelected=${this.visionSelected}
                .spouseDob=${this.spouseData.dob}
                .dentalChecked=${this.spouseData.dentalChecked}
                .visionChecked=${this.spouseData.visionChecked}
                @spouse-close=${this.handleSpouseClose}
                @spouse-data-change=${this.handleSpouseDataChange}
              >
              </spouse-card
            ></lh-container>`
          : nothing
      }
      ${repeat(
        this.dependentCards,
        (dependentId) => `dependent-${dependentId}`,
        (dependentId, index) => {
          const depData = this.dependentsData.get(dependentId)
          const depDob = depData?.dob || ''
          const depDentalChecked = depData?.dentalChecked
          const depVisionChecked = depData?.visionChecked
          return html`
            <lh-container
              gutters="none"
              margin-top=${this.isMobile ? 'xl' : 'sm'}
              context-id="dependent-card"
              ><dependent-card
                .dependentId=${dependentId}
                .displayIndex=${index + 1}
                .dentalSelected=${this.dentalSelected}
                .visionSelected=${this.visionSelected}
                .dependentDob=${depDob}
                .dentalChecked=${depDentalChecked}
                .visionChecked=${depVisionChecked}
                @dependent-close=${this.handleDependentClose}
                @dependent-data-change=${this.handleDependentDataChange}
              >
              </dependent-card>
            </lh-container>
          `
        }
      )}
        <lh-grid>
          <lh-grid-item>
            <lh-button-group
              size="md"
              orientation="horizontal"
              alignment="center"
            >
              ${
                !this.showSpouseCard
                  ? html`<lh-button
                      icon="user-plus"
                      icon-position="left"
                      ?full-width=${this.isMobile}
                      data-testid="spouse-toggle"
                      @click=${this.handleAddSpouse}
                      analytics-id="ancillary-rate-quote:spouse-domesticPartner:button-text"
                    >
                      Spouse / Domestic Partner
                    </lh-button>`
                  : nothing
              }
              <lh-button
                icon="user-plus"
                icon-position="left"
                ?disabled=${this.dependentCards.length >= 8}
                ?full-width=${this.isMobile}
                data-testid="add-dependent"
                analytics-id="ancillary-rate-quote:dependent:button-text"
                @click=${this.handleAddDependent}
              >
                Dependent
              </lh-button>
            </lh-button-group>
          </lh-grid-item>
        </lh-grid> 
        ${
          this.dependentCards.length >= 8
            ? html`<lh-container gutters="none" margin-top="xl">
                <div class="zip-warning">
                  <lh-typography size="md">
                    <strong>
                      If you need to add more dependents, please call
                      1-855-839-2583 for assistance.
                    </strong>
                  </lh-typography>
                </div>
              </lh-container>`
            : nothing
        }
        ${!this.isMobile ? html`<lh-divider></lh-divider>` : nothing}
        <lh-grid
          context-id="add-applicant-buttons"
          justify="end"
          margin-top="xl"
        >
          <lh-grid-item>
            <div class="rq-continue-button-container">
              <rq-continue-button
                .disabled=${this.continueButtonDisabled}
                @click=${this.handleContinue}
              >
              </rq-continue-button>
            </div>
          </lh-grid-item>
        </lh-grid>
        ${
          this.isLoading
            ? html`<div class="overlay"><lh-loader></lh-loader></div>`
            : nothing
        }
      </lh-container></lh-container
    >`
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ancillary-form': AncillaryForm
  }
}
